import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ZoomIn, ZoomOut, RotateCw, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Mobile-friendly Image Cropper Component
 * Supports pinch-to-zoom, pan, rotate, and crop
 */
const ImageCropper = ({ 
  imageSrc, 
  onSave, 
  onCancel, 
  aspectRatio = null, // e.g., { width: 16, height: 9 } for header, null for free crop
  minZoom = 1,
  maxZoom = 3,
  outputWidth = 1920,
  outputHeight = null // null = maintain aspect ratio
}) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Safety check - return early after hooks
  if (!imageSrc) {
    console.warn('ImageCropper: No imageSrc provided');
    return null;
  }

  // Calculate output height based on aspect ratio if provided
  const finalOutputHeight = outputHeight || (aspectRatio 
    ? Math.round(outputWidth / (aspectRatio.width / aspectRatio.height))
    : outputWidth);

  const handleZoom = (delta) => {
    setZoom(prev => {
      const newZoom = Math.max(minZoom, Math.min(maxZoom, prev + delta));
      return newZoom;
    });
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handlePanStart = (event) => {
    setIsDragging(true);
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    const clientY = event.touches ? event.touches[0].clientY : event.clientY;
    setDragStart({ x: clientX - position.x, y: clientY - position.y });
  };

  const handlePan = (event) => {
    if (!isDragging) return;
    
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    const clientY = event.touches ? event.touches[0].clientY : event.clientY;
    
    const newX = clientX - dragStart.x;
    const newY = clientY - dragStart.y;
    
    // Constrain panning to keep image within bounds
    if (containerRef.current && imageRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const imageRect = imageRef.current.getBoundingClientRect();
      
      const maxX = (imageRect.width * zoom - containerRect.width) / 2;
      const maxY = (imageRect.height * zoom - containerRect.height) / 2;
      
      setPosition({
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY))
      });
    }
  };

  const handlePanEnd = () => {
    setIsDragging(false);
  };

  // Pinch to zoom for mobile
  const touchStateRef = useRef({ distance: null, zoom: null });
  
  const handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      touchStateRef.current = { distance, zoom };
    } else {
      handlePanStart(event);
    }
  };

  const handleTouchMove = (event) => {
    if (event.touches.length === 2 && touchStateRef.current.distance) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      const scale = distance / touchStateRef.current.distance;
      const newZoom = Math.max(minZoom, Math.min(maxZoom, touchStateRef.current.zoom * scale));
      setZoom(newZoom);
    } else {
      handlePan(event);
    }
  };

  const handleTouchEnd = () => {
    handlePanEnd();
    touchStateRef.current = { distance: null, zoom: null };
  };

  const handleSave = useCallback(async () => {
    if (!imageSrc || !containerRef.current || !imageRef.current) {
      console.error('ImageCropper: Missing required refs or imageSrc');
      return;
    }
    
    setIsProcessing(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = outputWidth;
      canvas.height = finalOutputHeight;
      const ctx = canvas.getContext('2d');

      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = (err) => {
          console.error('Image load error:', err);
          reject(new Error('Failed to load image'));
        };
        img.src = imageSrc;
      });

      // Calculate crop area
      const containerRect = containerRef.current.getBoundingClientRect();
      const imageRect = imageRef.current.getBoundingClientRect();
      
      if (!containerRect || !imageRect) {
        throw new Error('Could not get element dimensions');
      }
      
      // Get the visible area in image coordinates
      const scaleX = img.width / imageRect.width;
      const scaleY = img.height / imageRect.height;
      
      const cropX = (containerRect.width / 2 - imageRect.width / 2 - position.x) * scaleX;
      const cropY = (containerRect.height / 2 - imageRect.height / 2 - position.y) * scaleY;
      const cropWidth = containerRect.width * scaleX / zoom;
      const cropHeight = containerRect.height * scaleY / zoom;

      // Create a temporary canvas for rotation
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      
      if (rotation !== 0) {
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
        tempCtx.rotate((rotation * Math.PI) / 180);
        tempCtx.drawImage(img, -img.width / 2, -img.height / 2);
      } else {
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        tempCtx.drawImage(img, 0, 0);
      }

      // Draw cropped and scaled image
      ctx.drawImage(
        tempCanvas,
        Math.max(0, cropX), Math.max(0, cropY), 
        Math.min(cropWidth, tempCanvas.width - cropX), 
        Math.min(cropHeight, tempCanvas.height - cropY),
        0, 0, canvas.width, canvas.height
      );

      canvas.toBlob((blob) => {
        if (blob && onSave) {
          onSave(blob);
        } else {
          console.error('Failed to create blob or onSave callback missing');
        }
        setIsProcessing(false);
      }, 'image/jpeg', 0.9);
    } catch (error) {
      console.error('Error processing image:', error);
      setIsProcessing(false);
      alert('Failed to process image. Please try again.');
    }
  }, [imageSrc, position, zoom, rotation, outputWidth, finalOutputHeight, onSave]);

  return (
    <div className="fixed inset-0 bg-black/90 z-[99999] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50 border-b border-gray-800">
        <h2 className="text-white font-semibold text-lg">Edit Image</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="text-white hover:bg-gray-800"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Image Container */}
      <div 
        ref={containerRef}
        className="flex-1 relative overflow-hidden flex items-center justify-center bg-gray-900"
        onMouseDown={handlePanStart}
        onMouseMove={handlePan}
        onMouseUp={handlePanEnd}
        onMouseLeave={handlePanEnd}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: 'none', userSelect: 'none' }}
      >
        {imageSrc && (
          <motion.img
            ref={imageRef}
            src={imageSrc}
            alt="Crop preview"
            className="max-w-none select-none"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${zoom}) rotate(${rotation}deg)`,
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
            drag={false}
            animate={{
              x: position.x,
              y: position.y,
              scale: zoom,
              rotate: rotation,
            }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            onError={(e) => {
              console.error('Image failed to load:', imageSrc);
              e.target.style.display = 'none';
            }}
          />
        )}
      </div>

      {/* Controls */}
      <div className="bg-black/50 border-t border-gray-800 p-4">
        <div className="flex items-center justify-between mb-4">
          {/* Zoom Controls */}
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleZoom(-0.1)}
              disabled={zoom <= minZoom}
              className="text-white border-gray-700 hover:bg-gray-800"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-white text-sm min-w-[60px] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleZoom(0.1)}
              disabled={zoom >= maxZoom}
              className="text-white border-gray-700 hover:bg-gray-800"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>

          {/* Rotate Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRotate}
            className="text-white border-gray-700 hover:bg-gray-800"
          >
            <RotateCw className="w-4 h-4 mr-2" />
            Rotate
          </Button>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1 border-gray-700 text-white hover:bg-gray-800"
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Processing...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Check className="w-4 h-4" />
                <span>Save</span>
              </div>
            )}
          </Button>
        </div>

        {/* Instructions */}
        <p className="text-gray-400 text-xs text-center mt-3">
          Pinch to zoom • Drag to pan • Tap rotate to rotate
        </p>
      </div>
    </div>
  );
};

export default ImageCropper;

